import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import FirstConnectionForm from "./FirstConnectionForm";

interface LoginFormProps {
  role: 'admin' | 'company' | 'employee';
}

const LoginForm = ({ role }: LoginFormProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'password' | 'first-connection'>('email');
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [checkingEmployee, setCheckingEmployee] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email requis",
        description: "Veuillez entrer votre adresse email"
      });
      return;
    }

    // For non-employee roles, go straight to password
    if (role !== 'employee') {
      setStep('password');
      return;
    }

    setCheckingEmployee(true);

    try {
      // Use SECURITY DEFINER function to check employee (bypasses RLS for unauthenticated users)
      const { data: employee, error } = await supabase
        .rpc('check_employee_by_email', { p_email: email.toLowerCase().trim() });

      if (error) throw error;

      if (!employee) {
        toast({
          variant: "destructive",
          title: "Compte non trouvé",
          description: "Aucun compte employé n'existe avec cet email. Contactez votre administrateur RH."
        });
        setCheckingEmployee(false);
        return;
      }
      
      // Cast the JSON response to proper type
      const employeeRecord = employee as { 
        id: string; 
        first_name: string; 
        last_name: string; 
        email: string; 
        profile_id: string | null; 
        invitation_status: string; 
        company_id: string; 
        companies: { name: string } 
      };
      
      setEmployeeData(employeeRecord);

      // If employee has a profile_id, they already have an account
      if (employeeRecord.profile_id) {
        setStep('password');
      } else {
        // New employee - show first connection form
        setStep('first-connection');
      }
    } catch (error: any) {
      console.error('Error checking employee:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer."
      });
    } finally {
      setCheckingEmployee(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (!roleData) {
          throw new Error(t('auth.login.roleNotFound'));
        }

        toast({
          title: t('auth.login.success'),
          description: `${t('auth.login.successDesc')} ${t(`auth.roles.${roleData.role}.title`).toLowerCase()}`,
        });

        switch (roleData.role) {
          case 'admin':
            navigate('/admin-dashboard');
            break;
          case 'company':
            navigate('/company-dashboard');
            break;
          case 'employee':
            navigate('/employee-dashboard');
            break;
          default:
            navigate('/');
        }
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('auth.login.error'),
        description: error.message || t('auth.login.errorDesc'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setEmployeeData(null);
  };

  // Show first connection form for new employees
  if (step === 'first-connection' && employeeData) {
    return (
      <FirstConnectionForm 
        email={email}
        employeeData={employeeData}
        onBack={handleBackToEmail}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.login.title')}</CardTitle>
        <CardDescription>
          {t('auth.login.description')} {t(`auth.roles.${role}.title`).toLowerCase()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={checkingEmployee}>
              {checkingEmployee ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Vérification...
                </>
              ) : (
                <>
                  Continuer
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
            
            {role === 'employee' && (
              <p className="text-xs text-center text-muted-foreground mt-4">
                Si c'est votre première connexion, vous pourrez créer votre mot de passe à l'étape suivante.
              </p>
            )}
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.login.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full"
              onClick={handleBackToEmail}
            >
              ← Modifier l'email
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default LoginForm;
